import React, { useEffect, useMemo, useState } from 'react';
import { FaCalendarAlt, FaLeaf, FaShoppingBasket, FaTimes, FaUtensils } from 'react-icons/fa';
import SlotWeeklyMenuPopover from './SlotWeeklyMenuPopover';
import WeeklyMenuTable from './WeeklyMenuTable';
import { WEEKLY_MENU_INDICATIVE_NOTE } from './weeklyMenuNotes';
import { formatPlanPriceWhole } from './planPricingUtils';
import './SpecialtyPlans.css';
import {
  EXTENDED_MEAL_SLOTS,
  GENERAL_REGION_KEYS,
  getPlanMenuSlotIds,
  getFullDayMealDiscountPercent,
  getSpecialtySlotMeals,
  isSpecialtySlotProductId,
  resolveFullPackageDayPricing,
  resolveSpecialtyPerDayPrice,
  resolveSpecialtyPlanForRegion,
  SPECIALTY_PLAN_CATALOG,
  isOrganicOnlySpecialtyPlan,
} from '../../data/specialtyMealPlans';
import { REGION_LABELS } from '../../data/specialtyCatalogApi';
import { BUILDER_SLOT_SHORT_NAME, BUILDER_SPECIALTY_THEME, resolveBuilderSpecialtyTheme } from '../../utils/builderWizardUi';
import { isMenuItemRelevantToSlot } from '../../utils/subscriptionCartItems';
import '../../pages/MealPlans/SubscriptionBuilderPreview.css';
import './AddItemsPicker.css';

export default function AddItemsPickerModal({
  open,
  onClose,
  specialtyPlans = SPECIALTY_PLAN_CATALOG,
  pickerIntent = 'full-day',
  editingProductId = null,
  builderPickerPlanId,
  builderPickerSlotId,
  setBuilderPickerPlanId,
  useOrganicPricing,
  setUseOrganicPricing,
  specialtyRegionKey,
  setSpecialtyRegionKey,
  selectedPlanPrice,
  onMealClick,
  onAddFullPackage,
  onApplySpecialtySlot,
  buildSpecialtySlotDraftProduct,
  builderPickerMode = 'plan',
  setBuilderPickerMode,
  slotDraftProducts = {},
  subscriptionProducts = [],
  ourMenuProductsLoading = false,
  otherSlotProductMap = {},
  onAddOtherProduct,
  onRemoveOtherProduct,
}) {
  const plans = specialtyPlans?.length ? specialtyPlans : SPECIALTY_PLAN_CATALOG;
  const catalogPlan = plans.find((p) => p.id === builderPickerPlanId) || plans[0];
  const displayPlan = resolveSpecialtyPlanForRegion(catalogPlan, specialtyRegionKey);
  const pickerSlot = EXTENDED_MEAL_SLOTS.find((s) => s.id === builderPickerSlotId) || EXTENDED_MEAL_SLOTS[0];
  const isFullDay = pickerIntent === 'full-day';
  const isAlacarte = builderPickerMode === 'others';
  const organicOnlyPlan = !isAlacarte && isOrganicOnlySpecialtyPlan(builderPickerPlanId);
  const organic = useOrganicPricing || organicOnlyPlan;

  const menuSlotIds = useMemo(
    () => (displayPlan ? getPlanMenuSlotIds(displayPlan) : []),
    [displayPlan],
  );

  const [menuPopupSlot, setMenuPopupSlot] = useState(null);
  const [menuQuery, setMenuQuery] = useState('');

  useEffect(() => {
    if (organicOnlyPlan && !useOrganicPricing) {
      setUseOrganicPricing(true);
    }
  }, [organicOnlyPlan, builderPickerPlanId, useOrganicPricing, setUseOrganicPricing]);

  useEffect(() => {
    if (!open) setMenuPopupSlot(null);
    setMenuQuery('');
  }, [open, builderPickerPlanId, builderPickerSlotId, specialtyRegionKey, useOrganicPricing]);

  const slotPrice = displayPlan
    ? resolveSpecialtyPerDayPrice(displayPlan, [pickerSlot.id], organic)
    : null;
  const fullPackagePricing = displayPlan && menuSlotIds.length
    ? resolveFullPackageDayPricing(displayPlan, menuSlotIds, organic)
    : null;

  const otherProducts = useMemo(() => {
    const allowedCategories = otherSlotProductMap[pickerSlot.id] || [];
    const query = menuQuery.trim().toLowerCase();
    return subscriptionProducts.filter((product) => {
      const relevant = isMenuItemRelevantToSlot(product, pickerSlot.id)
        || allowedCategories.includes(product.category);
      if (!relevant) return false;
      if (!query) return true;
      const haystack = `${product.name || ''} ${product.category || ''} ${product.menuName || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [subscriptionProducts, otherSlotProductMap, pickerSlot.id, menuQuery]);

  const alacarteSlotItems = useMemo(() => {
    const allowedCategories = otherSlotProductMap[pickerSlot.id] || [];
    return (slotDraftProducts[pickerSlot.id] || []).filter((item) => {
      if (!item || item.isFullDayPackage || item.category === 'Specialty plan' || isSpecialtySlotProductId(item.id)) {
        return false;
      }
      if (allowedCategories.includes(item.category)) return true;
      return subscriptionProducts.some((product) => product.id === item.id);
    });
  }, [slotDraftProducts, pickerSlot.id, otherSlotProductMap, subscriptionProducts]);

  const alacarteSlotItemCount = alacarteSlotItems.reduce(
    (sum, item) => sum + Number(item.quantity || 1),
    0,
  );
  const alacarteSlotTotal = alacarteSlotItems.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
    0,
  );

  const selectSpecialtyPlan = (planId) => {
    setBuilderPickerMode?.('plan');
    setBuilderPickerPlanId(planId);
  };

  const selectAlacarte = () => {
    setBuilderPickerMode?.('others');
  };

  const confirmPicker = () => {
    if (isAlacarte) {
      onClose();
      return;
    }
    if (!displayPlan) return;
    if (isFullDay) {
      onAddFullPackage(displayPlan, menuSlotIds, organic);
    } else {
      onApplySpecialtySlot(
        pickerSlot.id,
        buildSpecialtySlotDraftProduct(catalogPlan, pickerSlot.id, organic, specialtyRegionKey),
        editingProductId,
      );
    }
    onClose();
  };

  const openWeeklyMenu = () => {
    if (!displayPlan) return;
    if (isFullDay) {
      setMenuPopupSlot({
        fullDay: true,
        slotLabel: 'Full Day',
        meals: [],
        price: fullPackagePricing?.chargePerDay ?? selectedPlanPrice,
      });
      return;
    }
    const slotId = pickerSlot.id;
    const slotMeta = EXTENDED_MEAL_SLOTS.find((s) => s.id === slotId) || pickerSlot;
    const meals = getSpecialtySlotMeals(displayPlan, slotId);
    if (!meals.length) return;
    const price = resolveSpecialtyPerDayPrice(displayPlan, [slotId], organic) || selectedPlanPrice;
    setMenuPopupSlot({
      slotId,
      slotLabel: BUILDER_SLOT_SHORT_NAME[slotMeta.id] || slotMeta.label,
      meals,
      price,
    });
  };

  if (!open) return null;

  const slotTitle = BUILDER_SLOT_SHORT_NAME[pickerSlot.id] || pickerSlot.label;
  const confirmLabel = isAlacarte
    ? 'Done'
    : (isFullDay ? 'Add Full Day' : (editingProductId ? 'Update Slot' : 'Add Slot'));
  const footerMeta = isAlacarte
    ? `Our menu · ${pickerSlot.label}${alacarteSlotItemCount ? ` · ${alacarteSlotItemCount} added` : ''}`
    : (isFullDay
      ? `Applies to all ${menuSlotIds.length || 5} meal slots`
      : slotTitle);
  const footerPrice = isAlacarte
    ? (alacarteSlotItemCount > 0 ? formatPlanPriceWhole(alacarteSlotTotal) : null)
    : (isFullDay
      ? (fullPackagePricing?.chargePerDay != null ? `${formatPlanPriceWhole(fullPackagePricing.chargePerDay)}/day` : null)
      : (slotPrice != null ? `${formatPlanPriceWhole(slotPrice)}/day` : null));

  return (
    <div
      className="sbp-compare-modal-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="sbp-compare-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mp-add-items-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span>{isAlacarte ? 'Our menu' : (isFullDay ? 'Full day' : 'Packaged slot')}</span>
            <h2 id="mp-add-items-title">
              {isAlacarte
                ? `Add from our menu · ${slotTitle}`
                : (isFullDay ? 'Add Full Day' : `${editingProductId ? 'Change' : 'Add'} ${slotTitle}`)}
            </h2>
            <p>
              {isAlacarte
                ? `Choose individual dishes for ${pickerSlot.label}. Our menu is Regular only for now.`
                : (isFullDay
                  ? 'Choose one meal style for all 5 slots.'
                  : 'Choose a specialty package, or pick individual dishes from our menu.')}
            </p>
          </div>
          <button type="button" aria-label="Close meal picker" onClick={onClose}>
            <FaTimes />
          </button>
        </header>

        <div className="sbp-compare-modal__body">
          <section>
            <h3>Specialty type</h3>
            <div className={`sbp-compare-specialties${!isFullDay ? ' sbp-compare-specialties--with-alacarte' : ''}`}>
              {plans.map((plan) => {
                const Icon = plan.iconComponent || FaUtensils;
                const theme = resolveBuilderSpecialtyTheme(plan.id) || BUILDER_SPECIALTY_THEME.general;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    className={!isAlacarte && builderPickerPlanId === plan.id ? 'is-active' : ''}
                    style={{
                      '--sbp-specialty': theme.color,
                      '--sbp-specialty-soft': theme.soft,
                    }}
                    onClick={() => selectSpecialtyPlan(plan.id)}
                  >
                    <span><Icon aria-hidden /></span>
                    <strong>{plan.name}</strong>
                  </button>
                );
              })}
              {!isFullDay ? (
                <button
                  type="button"
                  className={isAlacarte ? 'is-active' : ''}
                  style={{
                    '--sbp-specialty': '#5c534c',
                    '--sbp-specialty-soft': '#f4f1ec',
                  }}
                  onClick={selectAlacarte}
                >
                  <span><FaShoppingBasket aria-hidden /></span>
                  <strong>Our menu</strong>
                </button>
              ) : null}
            </div>
          </section>

          {isAlacarte ? null : (
            <section>
              <h3>Meal type</h3>
              <div className="sbp-choice-pills" role="group" aria-label="Regular or organic">
                <button
                  type="button"
                  className={!organic ? 'is-active' : ''}
                  disabled={organicOnlyPlan}
                  onClick={() => setUseOrganicPricing(false)}
                >
                  Regular
                </button>
                <button
                  type="button"
                  className={`is-organic${organic ? ' is-active' : ''}`}
                  onClick={() => setUseOrganicPricing(true)}
                >
                  <FaLeaf aria-hidden /> Organic
                </button>
              </div>
            </section>
          )}

          {isAlacarte ? (
            <section className="mp-add-items-alacarte-inline">
              <h3>Menu items · {pickerSlot.label}</h3>
              <p className="sbp-compare-cuisine__hint">
                Regular items only. Organic is not available on our menu yet.
              </p>
              <label className="mp-add-items-alacarte__search">
                <input
                  type="search"
                  value={menuQuery}
                  onChange={(event) => setMenuQuery(event.target.value)}
                  placeholder={`Search ${pickerSlot.label.toLowerCase()} dishes`}
                  aria-label={`Search ${pickerSlot.label} dishes`}
                />
              </label>
              <div className="mp-add-items-alacarte__items-scroll">
                {ourMenuProductsLoading && otherProducts.length === 0 ? (
                  <p className="mp-add-items-alacarte__empty">Loading our menu…</p>
                ) : otherProducts.length === 0 ? (
                  <p className="mp-add-items-alacarte__empty">
                    {menuQuery.trim()
                      ? `No dishes match “${menuQuery.trim()}”.`
                      : `No menu items for ${pickerSlot.label} yet.`}
                  </p>
                ) : (
                  <ul className="mp-add-items-others-list">
                    {otherProducts.map((product) => {
                      const draftItem = (slotDraftProducts[pickerSlot.id] || []).find((item) => item.id === product.id);
                      return (
                        <li
                          key={`${pickerSlot.id}-${product.id}`}
                          className={`mp-add-items-others-row${draftItem ? ' mp-add-items-others-row--added' : ''}`}
                        >
                          <button
                            type="button"
                            className="mp-add-items-others-row__info"
                            onClick={() => onMealClick?.({
                              ...product,
                              isOrganic: false,
                              slotLabel: pickerSlot.label,
                              availability: 'All days',
                            })}
                          >
                            <span
                              className="mp-add-items-others-type mp-add-items-others-type--regular"
                              aria-label="Regular"
                              title="Regular"
                            >
                              <FaUtensils aria-hidden />
                            </span>
                            <span className="mp-add-items-others-row__copy">
                              <span className="mp-add-items-others-row__name">{product.name}</span>
                              {product.category ? (
                                <small className="mp-add-items-others-row__category">{product.category}</small>
                              ) : null}
                            </span>
                            <span className="mp-add-items-others-row__price">
                              {formatPlanPriceWhole(product.price)}
                            </span>
                          </button>
                          {draftItem ? (
                            <button
                              type="button"
                              className="mp-add-items-others-row__btn mp-add-items-others-row__btn--remove"
                              onClick={() => onRemoveOtherProduct?.(pickerSlot.id, product.id)}
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="mp-add-items-others-row__btn"
                              onClick={() => onAddOtherProduct?.(pickerSlot.id, { ...product, isOrganic: false })}
                            >
                              Add
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
          ) : (
            <>
              <section>
                <h3>Cuisine</h3>
                <div
                  className={`sbp-choice-pills${builderPickerPlanId === 'general' ? '' : ' is-disabled'}`}
                  role="group"
                  aria-label="Cuisine style"
                  aria-disabled={builderPickerPlanId !== 'general'}
                >
                  {GENERAL_REGION_KEYS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={builderPickerPlanId === 'general' && specialtyRegionKey === key ? 'is-active' : ''}
                      disabled={builderPickerPlanId !== 'general'}
                      onClick={() => setSpecialtyRegionKey(key)}
                    >
                      {(REGION_LABELS[key] || key).replace(/ Meal$/i, '')}
                    </button>
                  ))}
                </div>
                <p className="sbp-compare-cuisine__hint">Cuisine applies to General meals.</p>
              </section>

              {displayPlan ? (
                <button type="button" className="mp-add-items-compare-menu" onClick={openWeeklyMenu}>
                  View weekly menu
                  {fullPackagePricing?.savingsPerDay > 0 && isFullDay
                    ? ` · ${getFullDayMealDiscountPercent(displayPlan)}% off full day`
                    : ''}
                </button>
              ) : null}
            </>
          )}
        </div>

        <footer>
          <span>
            {footerMeta}
            {footerPrice ? ` · ${footerPrice}` : ''}
          </span>
          <button type="button" className="sbp-compare-modal__confirm" onClick={confirmPicker} disabled={!isAlacarte && !displayPlan}>
            {confirmLabel}
          </button>
        </footer>
      </div>

      {menuPopupSlot?.fullDay && displayPlan ? (
        <div
          className="mp-slot-menu-popover"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setMenuPopupSlot(null);
          }}
        >
          <div
            className={`mp-slot-menu-popover__card mp-slot-menu-popover__card--full-day mp-slot-menu-popover__card--${organic ? 'organic' : 'regular'}`}
            role="dialog"
            aria-labelledby="mp-full-day-menu-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mp-slot-menu-popover__accent" aria-hidden />
            <header className="mp-slot-menu-popover__header">
              <div className="mp-slot-menu-popover__header-text">
                <span className="mp-slot-menu-popover__eyebrow">
                  <FaCalendarAlt aria-hidden />
                  Sample weekly menu
                </span>
                <h3 id="mp-full-day-menu-title">Full Day</h3>
                <div className="mp-slot-menu-popover__chips">
                  {displayPlan.name ? <span className="mp-slot-menu-popover__chip">{displayPlan.name}</span> : null}
                  {displayPlan.activeRegionLabel ? (
                    <span className="mp-slot-menu-popover__chip">{displayPlan.activeRegionLabel}</span>
                  ) : null}
                  {menuPopupSlot.price != null ? (
                    <span className="mp-slot-menu-popover__chip mp-slot-menu-popover__chip--price">
                      {formatPlanPriceWhole(menuPopupSlot.price)}
                      <small>/day</small>
                    </span>
                  ) : null}
                  <span className={`mp-slot-menu-popover__chip mp-slot-menu-popover__chip--type mp-slot-menu-popover__chip--${organic ? 'organic' : 'regular'}`}>
                    {organic ? <FaLeaf aria-hidden /> : <FaUtensils aria-hidden />}
                    {organic ? 'Organic' : 'Regular'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="mp-slot-menu-popover__close"
                onClick={() => setMenuPopupSlot(null)}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </header>
            <div className="mp-slot-menu-popover__body mp-slot-menu-popover__body--full-day">
              <WeeklyMenuTable
                plan={displayPlan}
                orientation="byDay"
                useOrganic={organic}
              />
            </div>
            <footer className="mp-slot-menu-popover__footer">
              <p className="mp-slot-menu-popover__indicative-note" role="note">
                <strong>Please note:</strong> {WEEKLY_MENU_INDICATIVE_NOTE}
              </p>
            </footer>
          </div>
        </div>
      ) : menuPopupSlot ? (
        <SlotWeeklyMenuPopover
          planName={displayPlan?.name}
          slotLabel={menuPopupSlot.slotLabel}
          slotPrice={menuPopupSlot.price}
          meals={menuPopupSlot.meals}
          useOrganic={organic}
          onClose={() => setMenuPopupSlot(null)}
          onMealClick={onMealClick}
        />
      ) : null}
    </div>
  );
}
